import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getEquipmentAreas, getIssues, createIssue, deleteIssue } from '../services/maintenanceService';
import ScrollHint from '../components/ScrollHint';
import { useLocationContext } from '../contexts/LocationContext';
import { useEquipmentGroups } from '../utils/equipmentGroups';

const getEquipmentLabel = (issue) =>
  issue.equipment_area?.name || (issue.equipment_category?.length > 0 ? issue.equipment_category.join(', ') : '-');

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr + 'T12:00:00').toLocaleDateString();
};

const formatLoggedAt = (isoStr) => {
  if (!isoStr) return '-';
  const d = new Date(isoStr.endsWith('Z') ? isoStr : isoStr + 'Z');
  return isNaN(d) ? '-' : d.toLocaleString();
};

const DetailRow = ({ label, children }) => (
  <div>
    <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
    <div className="text-sm text-gray-900 mt-0.5">{children}</div>
  </div>
);

const ViewModal = ({ issue, onClose, onDelete }) => (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
      <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wide text-gray-500">Customer Issue</div>
          <h2 className="text-xl font-bold text-gray-900 mt-0.5">
            {formatDate(issue.incident_date)}
            {issue.incident_time && <span className="text-gray-500 font-normal"> at {issue.incident_time}</span>}
          </h2>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 flex-shrink-0" title="Close">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">
        <DetailRow label="Equipment">{getEquipmentLabel(issue)}</DetailRow>

        <DetailRow label="Description">
          <p className="whitespace-pre-wrap">{issue.description}</p>
        </DetailRow>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <DetailRow label="Customer Name">{issue.customer_name || <span className="text-gray-400">Not provided</span>}</DetailRow>
          <DetailRow label="Customer Contact">{issue.customer_contact || <span className="text-gray-400">Not provided</span>}</DetailRow>
        </div>

        <DetailRow label="Work Order">
          {issue.work_order ? (
            <Link to={`/maintenance/work-order/${issue.work_order.id}`} className="text-blue-600 hover:text-blue-800">
              WO #{issue.work_order.id} — {issue.work_order.title} ({issue.work_order.status})
            </Link>
          ) : (
            <span className="text-gray-400">None linked</span>
          )}
        </DetailRow>

        <DetailRow label="Logged">{formatLoggedAt(issue.created_at)}</DetailRow>
      </div>

      <div className="px-6 py-3 border-t border-gray-200 flex justify-end gap-2">
        <button
          onClick={onDelete}
          className="text-red-600 hover:bg-red-50 px-3 py-1.5 rounded text-sm font-medium"
        >
          Delete
        </button>
        {!issue.work_order && (
          <Link
            to={`/maintenance/work-order/new?type=repair&from_issue=${issue.id}`}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded text-sm font-medium"
          >
            + Work Order
          </Link>
        )}
        <button
          onClick={onClose}
          className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-1.5 rounded text-sm font-medium"
        >
          Close
        </button>
      </div>
    </div>
  </div>
);

const CustomerIssues = () => {
  const { currentLocation } = useLocationContext();
  const equipmentCategories = useEquipmentGroups();
  const today = new Date().toISOString().split('T')[0];

  // Form state
  const [formData, setFormData] = useState({
    equipment_area_id: '',
    equipment_categories: [],
    description: '',
    incident_date: today,
    incident_time: '',
    customer_name: '',
    customer_contact: '',
  });
  const [equipmentMode, setEquipmentMode] = useState('unit');
  const [equipmentAreas, setEquipmentAreas] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // List state
  const [issues, setIssues] = useState([]);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [viewingIssue, setViewingIssue] = useState(null);

  useEffect(() => {
    fetchEquipmentAreas();
    fetchIssues();
  }, [currentLocation?.id]);

  useEffect(() => {
    fetchIssues();
  }, [filterCategory, filterStartDate, filterEndDate, filterSearch, currentLocation?.id]);

  const fetchEquipmentAreas = async () => {
    try {
      const data = await getEquipmentAreas();
      setEquipmentAreas(data.equipment_areas);
    } catch (err) {
      // Non-critical
    }
  };

  const fetchIssues = async () => {
    try {
      setListLoading(true);
      const params = {};
      if (filterCategory) params.equipment_category = filterCategory;
      if (filterStartDate) params.start_date = filterStartDate;
      if (filterEndDate) params.end_date = filterEndDate;
      if (filterSearch) params.search = filterSearch;
      const data = await getIssues(params);
      setIssues(data.issues || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to load issues', err);
    } finally {
      setListLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleCategory = (catValue) => {
    setFormData(prev => {
      const cats = prev.equipment_categories;
      if (cats.includes(catValue)) {
        return { ...prev, equipment_categories: cats.filter(c => c !== catValue) };
      } else {
        return { ...prev, equipment_categories: [...cats, catValue] };
      }
    });
  };

  const handleModeChange = (mode) => {
    setEquipmentMode(mode);
    if (mode === 'unit') {
      setFormData(prev => ({ ...prev, equipment_categories: [] }));
    } else {
      setFormData(prev => ({ ...prev, equipment_area_id: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSuccessMsg('');

    if (!formData.description.trim()) {
      setFormError('Description is required');
      return;
    }
    if (!formData.incident_date) {
      setFormError('Incident date is required');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        description: formData.description,
        incident_date: formData.incident_date,
        incident_time: formData.incident_time || null,
        customer_name: formData.customer_name,
        customer_contact: formData.customer_contact,
        equipment_area_id: equipmentMode === 'unit' ? (formData.equipment_area_id || null) : null,
        equipment_category: equipmentMode === 'group' && formData.equipment_categories.length > 0
          ? formData.equipment_categories
          : null,
      };
      await createIssue(payload);
      setSuccessMsg('Issue logged successfully');
      setFormData({
        equipment_area_id: '',
        equipment_categories: [],
        description: '',
        incident_date: today,
        incident_time: '',
        customer_name: '',
        customer_contact: '',
      });
      setEquipmentMode('unit');
      fetchIssues();
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      setFormError('Failed to log issue. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this issue?')) return;
    try {
      await deleteIssue(id);
      setViewingIssue(prev => (prev?.id === id ? null : prev));
      fetchIssues();
    } catch (err) {
      console.error('Failed to delete issue', err);
    }
  };

  // Group equipment areas by category for optgroup
  const groupedAreas = equipmentAreas.reduce((acc, area) => {
    if (!acc[area.category]) acc[area.category] = [];
    acc[area.category].push(area);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Customer Issues</h1>
        <Link
          to="/maintenance"
          className="text-gray-600 hover:text-gray-800 text-sm font-medium"
        >
          Back to Maintenance
        </Link>
      </div>

      {/* Log New Issue Form */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Log New Issue</h2>

        {successMsg && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
            {successMsg}
          </div>
        )}
        {formError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {formError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Equipment Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Equipment</label>
            <div className="flex items-center gap-4 mb-3">
              <button
                type="button"
                onClick={() => handleModeChange('unit')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  equipmentMode === 'unit'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Specific Unit
              </button>
              <button
                type="button"
                onClick={() => handleModeChange('group')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  equipmentMode === 'group'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Equipment Group(s)
              </button>
            </div>

            {equipmentMode === 'unit' ? (
              <select
                name="equipment_area_id"
                value={formData.equipment_area_id}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">General (no specific area)</option>
                {Object.entries(groupedAreas).map(([category, areas]) => (
                  <optgroup key={category} label={category}>
                    {areas.map(area => (
                      <option key={area.id} value={area.id}>{area.name}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {equipmentCategories.map(cat => (
                  <label
                    key={cat.value}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                      formData.equipment_categories.includes(cat.value)
                        ? 'bg-blue-50 border-blue-300 text-blue-800'
                        : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.equipment_categories.includes(cat.value)}
                      onChange={() => toggleCategory(cat.value)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium">{cat.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="What did the customer report? e.g. Changer didn't dispense coins"
            />
          </div>

          {/* Date + Time row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Incident Date *</label>
              <input
                type="date"
                name="incident_date"
                value={formData.incident_date}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Incident Time</label>
              <input
                type="time"
                name="incident_time"
                value={formData.incident_time}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Customer Name + Contact */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name</label>
              <input
                type="text"
                name="customer_name"
                value={formData.customer_name}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Optional"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer Contact</label>
              <input
                type="text"
                name="customer_contact"
                value={formData.customer_contact}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Phone or email (optional)"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Log Issue'}
          </button>
        </form>
      </div>

      {/* Issue Log Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">
            Issue Log
            {total > 0 && <span className="text-sm font-normal text-gray-500 ml-2">({total})</span>}
          </h2>
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
            >
              <option value="">All Equipment</option>
              {equipmentCategories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
            <input
              type="date"
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
              placeholder="From"
            />
            <span className="text-gray-500 text-sm">to</span>
            <input
              type="date"
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
              placeholder="To"
            />
            <input
              type="text"
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              placeholder="Search description..."
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm flex-1 min-w-[180px]"
            />
          </div>
        </div>

        {listLoading ? (
          <div className="p-6 text-center text-gray-500">Loading...</div>
        ) : issues.length > 0 ? (
          <ScrollHint>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Equipment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Work Order</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {issues.map(issue => (
                  <tr key={issue.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(issue.incident_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {issue.incident_time || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {getEquipmentLabel(issue)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                      <button
                        onClick={() => setViewingIssue(issue)}
                        className="text-left w-full truncate hover:text-blue-700 hover:underline"
                        title={issue.description}
                      >
                        {issue.description}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {issue.customer_name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {issue.work_order ? (
                        <Link
                          to={`/maintenance/work-order/${issue.work_order.id}`}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          WO #{issue.work_order.id} ({issue.work_order.status})
                        </Link>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                      <button
                        onClick={() => setViewingIssue(issue)}
                        className="text-blue-600 hover:text-blue-800"
                        title="View Details"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleDelete(issue.id)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete"
                      >
                        Delete
                      </button>
                      {!issue.work_order && (
                        <Link
                          to={`/maintenance/work-order/new?type=repair&from_issue=${issue.id}`}
                          className="text-green-600 hover:text-green-800"
                          title="Create Work Order"
                        >
                          + Work Order
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollHint>
        ) : (
          <div className="p-6 text-center text-gray-500">
            No issues logged yet.
          </div>
        )}
      </div>

      {viewingIssue && (
        <ViewModal
          issue={viewingIssue}
          onClose={() => setViewingIssue(null)}
          onDelete={() => handleDelete(viewingIssue.id)}
        />
      )}
    </div>
  );
};

export default CustomerIssues;
